import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './authform.component.css';
import { login } from '../../service/auth.service';
import { checkEmail } from '../../service/user.service';
import Navbar from "../Common/Navbar";
import profileService from '../../service/profile.service';

const AuthFormComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    mobile: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Map backend error codes/messages to user-friendly messages
  const getFriendlyErrorMessage = (backendMessage) => {
    switch (backendMessage) {
      case 'error.emailexists':
        return 'Email already registered. Please login instead.';
      case 'error.invalidpassword':
        return 'Password is invalid. Please follow the password rules.';
      case 'error.usernotfound':
        return 'User not found. Please sign up first.';
      default:
        return 'Signup failed. Please try again.';
    }
  };

  // Toast component
  const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
      const timer = setTimeout(() => {
        onClose();
      }, 4000);

      return () => clearTimeout(timer);
    }, [onClose]);

    const getIcon = () => {
      switch (type) {
        case 'success':
          return 'fa-check-circle';
        case 'error':
          return 'fa-times-circle';
        case 'warning':
          return 'fa-exclamation-triangle';
        default:
          return 'fa-info-circle';
      }
    };

    return (
      <div className={`toast-notification toast-${type}`}>
        <div className="toast-icon">
          <i className={`fas ${getIcon()}`}></i>
        </div>
        <div className="toast-content">
          <p className="toast-message">{message}</p>
        </div>
        <button className="toast-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>
    );
  };

  // Toast helper function
  const showToast = (message, type) => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Password validation rules
  const passwordRules = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSpecial: /[@#$!%*?&]/.test(formData.password)
  };

  const isPasswordValid = Object.values(passwordRules).every(rule => rule);

  // Handle input change for all fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear email error when user starts typing in email field
    if (name === 'email') {
      setEmailError('');
    }
  };

  // Handle email blur - check email existence
  const handleEmailBlur = async () => {
    const email = formData.email.trim();

    // Skip validation if email is empty
    if (!email) {
      setEmailError('');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      showToast('Please enter a valid email address', 'warning');
      return;
    }

    // Only check email existence for SIGNUP, not for LOGIN
    if (!isLogin) {
      setIsCheckingEmail(true);
      setEmailError('');

      try {
        const exists = await checkEmail(email);

        // For SIGNUP: email must NOT exist
        if (exists) {
          setEmailError('Email already registered. Please login instead.');
          showToast('Email already registered. Please login instead.', 'error');
        }
      } catch (error) {
        console.error('Email check error:', error);
        showToast('Failed to verify email. Please try again.', 'error');
        setEmailError('Failed to verify email');
      } finally {
        setIsCheckingEmail(false);
      }
    }
  };

  useEffect(() => {
    setEmailError('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      mobile: ''
    });
  }, [isLogin]);


  useEffect(() => {
    if (loginSuccess) {
      const redirectPath = localStorage.getItem('redirectAfterLogin');

      // Dispatch auth change event FIRST
      window.dispatchEvent(new Event("authChange"));

      if (redirectPath) {
        // User was trying to access something before login
        localStorage.removeItem('redirectAfterLogin');
        console.log('✅ User was trying to access:', redirectPath, '- redirecting there');

        // Mark that we're doing a special redirect
        sessionStorage.setItem('skipProfileLoad', 'true');

        // Navigate immediately to the page they wanted
        navigate(redirectPath, { replace: true });
      } else {
        // Normal login flow - go to profile
        console.log('✅ Normal login - going to profile');

        // Small delay for profile to load properly
        setTimeout(() => {
          navigate('/');
        }, 500);
      }
    }
  }, [loginSuccess, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailError) {
      showToast('Please fix the errors before submitting', 'warning');
      return;
    }

    // In authform.component.jsx - handleSubmit function (LOGIN section)

    if (isLogin) {
      // Handle login
      const dto = {
        username: formData.email,
        password: formData.password
      };

      try {
        const response = await login(dto);

        if (response && response.token) {
  // 🔐 Store token
  localStorage.setItem("token", response.token);

  // ⏳ Token expiry (1 hour)
  localStorage.setItem(
    "tokenExpiry",
    Date.now() + 60 * 60 * 1000
  );

  // ✅ FETCH FULL NAME FROM PROFILE
  let fullName = response.username; // Fallback to username
  
  try {
    const profileData = await profileService.getProfile();
    fullName = profileData.name || response.username;
    console.log('✅ Fetched full name:', fullName);
  } catch (profileError) {
    console.error('Failed to fetch profile:', profileError);
    fullName = response.username; // Fallback
  }

  // 🧑 User data with FULL NAME
  const userData = {
    username: response.username,
    name: fullName,  // ✅ NOW INCLUDES FULL NAME
    email: response.email,
    loginId: response.loginId || null
  };

  localStorage.setItem("userData", JSON.stringify(userData));

  if (response.loginId) {
    localStorage.setItem("loginId", response.loginId);
  }

  // 🔑 ROLE LOGIC - Store the backend role AS-IS
  localStorage.setItem("role", response.role);

  console.log("✅ Login successful! Role:", response.role);

  // 🔔 Dispatch auth change event
  window.dispatchEvent(new Event("authChange"));

  // 🔁 ADMIN vs USER redirect
  if (response.role === "ROLE_ADMIN") {
    navigate("/admin/dashboard", { replace: true });
  } else {
    setLoginSuccess(true);
  }

} else {
  showToast('Authentication failed. Please check your credentials.', 'error');
}
      } catch (error) {
        console.error('Login error:', error);
        showToast('Login failed. Please try again.', 'error');
      }
    } else {
      if (formData.password !== formData.confirmPassword) {
        showToast('Passwords do not match', 'warning');
        return;
      }

      if (!isPasswordValid) {
        showToast('Password does not meet the required criteria', 'warning');
        return;
      }

      try {
        const signupPayload = {
          actionType: 'INSERT',
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: parseInt(formData.mobile, 10)
        };

        const response = await fetch('http://localhost:8082/api/seo-user', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(signupPayload)
        });

        const data = await response.json().catch(() => null);

        if (response.ok) {
          showToast('Signup successful! Please login.', 'success');

          setTimeout(() => {
            setIsLogin(true);
            setFormData({
              email: '',
              password: '',
              confirmPassword: '',
              name: '',
              mobile: ''
            });
          }, 1000);
        } else {
          const backendMsg = data?.message || data?.title;
          const friendlyMsg = getFriendlyErrorMessage(backendMsg);
          showToast(friendlyMsg, 'error');
        }
      } catch (error) {
        console.error('Signup error:', error);
        showToast('Signup failed. Please try again.', 'error');
      }
    }
  };

  return (
    <>
      <Navbar />

      <div className="min-vh-100 d-flex align-items-center auth-container cs-authdiv">

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={closeToast}
          />
        )}

        {/* Background Logo */}
        <div className="background-logo">
          <div className="logo-text">eLexyt</div>
          <div className="logo-tagline">Where Technology Meets Tomorrow</div>
        </div>

        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0 cs-radius">
                <div className="card-body p-5">

                  {/* Logo/Brand */}
                  <div className="text-center mb-4">
                    <img
                      src="logo1.png"
                      alt="ElexytSEO"
                      width="80"
                      height="60"
                      className="mb-3"
                    />
                    <h2 className="fw-bold text-dark mb-2">
                      {isLogin ? 'Welcome Back' : 'Get Started'}
                    </h2>
                    <p className="text-muted">
                      {isLogin ? 'Sign in to your account' : 'Create your account'}
                    </p>
                  </div>

                  {/* Toggle Buttons */}
                  <div className="d-flex mb-4 cs-toggle">
                    <button
                      type="button"
                      className={`btn flex-fill cs-btn-radius ${isLogin ? 'btn-primary' : 'btn-light'
                        }`}
                      onClick={() => setIsLogin(true)}
                    >
                      Login
                    </button>

                    <button
                      type="button"
                      className={`btn flex-fill cs-btn-radius ${!isLogin ? 'btn-primary' : 'btn-light'
                        }`}
                      onClick={() => setIsLogin(false)}
                    >
                      Sign Up
                    </button>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit}>
                    {!isLogin && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="name" className="form-label">
                            Full Name
                          </label>
                          <input
                            type="text"
                            className="form-control cs-input"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required={!isLogin}
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="mobile" className="form-label">
                            Mobile Number
                          </label>
                          <input
                            type="tel"
                            className="form-control cs-input"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            required={!isLogin}
                            pattern="[0-9]{10}"
                            placeholder="Enter 10-digit number"
                          />
                        </div>
                      </>
                    )}

                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className={`form-control cs-input ${emailError ? 'is-invalid' : ''
                          }`}
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleEmailBlur}
                        required
                        disabled={isCheckingEmail}
                      />
                      {isCheckingEmail && (
                        <small className="text-muted">Checking email...</small>
                      )}
                      {emailError && (
                        <div className="invalid-feedback d-block">
                          {emailError}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">
                        Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control cs-input password-input"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                        <i
                          className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'
                            } password-toggle-icon`}
                          onClick={() => setShowPassword(!showPassword)}
                        ></i>
                      </div>
                    </div>

                    {/* Password Requirements */}
                    {!isLogin && formData.password && (
                      <div className="mb-3 password-requirements">
                        <p className="mb-1 fw-bold text-muted">
                          Password must contain:
                        </p>

                        <div className="requirements-list">
                          <div
                            className={
                              passwordRules.minLength
                                ? 'requirement-met'
                                : 'requirement-unmet'
                            }
                          >
                            <i
                              className={`fas ${passwordRules.minLength
                                ? 'fa-check-circle'
                                : 'fa-times-circle'
                                }`}
                            ></i>
                            At least 8 characters
                          </div>

                          <div
                            className={
                              passwordRules.hasUppercase
                                ? 'requirement-met'
                                : 'requirement-unmet'
                            }
                          >
                            <i
                              className={`fas ${passwordRules.hasUppercase
                                ? 'fa-check-circle'
                                : 'fa-times-circle'
                                }`}
                            ></i>
                            One uppercase letter
                          </div>

                          <div
                            className={
                              passwordRules.hasLowercase
                                ? 'requirement-met'
                                : 'requirement-unmet'
                            }
                          >
                            <i
                              className={`fas ${passwordRules.hasLowercase
                                ? 'fa-check-circle'
                                : 'fa-times-circle'
                                }`}
                            ></i>
                            One lowercase letter
                          </div>

                          <div
                            className={
                              passwordRules.hasNumber
                                ? 'requirement-met'
                                : 'requirement-unmet'
                            }
                          >
                            <i
                              className={`fas ${passwordRules.hasNumber
                                ? 'fa-check-circle'
                                : 'fa-times-circle'
                                }`}
                            ></i>
                            One number
                          </div>

                          <div
                            className={
                              passwordRules.hasSpecial
                                ? 'requirement-met'
                                : 'requirement-unmet'
                            }
                          >
                            <i
                              className={`fas ${passwordRules.hasSpecial
                                ? 'fa-check-circle'
                                : 'fa-times-circle'
                                }`}
                            ></i>
                            One special character (@#$!%*?&)
                          </div>
                        </div>
                      </div>
                    )}

                    {!isLogin && (
                      <div className="mb-4">
                        <label htmlFor="confirmPassword" className="form-label">
                          Confirm Password
                        </label>
                        <div className="position-relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-control cs-input password-input"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required={!isLogin}
                          />
                          <i
                            className={`fas ${showConfirmPassword
                              ? 'fa-eye-slash'
                              : 'fa-eye'
                              } password-toggle-icon`}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          ></i>
                        </div>
                      </div>
                    )}

                    {isLogin && (
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <Link to="/forgot-password" className="text-decoration-none">
                            Forgot password?
                          </Link>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary w-100 py-3 mb-3 cs-loginbtn"
                      disabled={!!emailError || isCheckingEmail}
                    >
                      {isLogin ? 'Sign In' : 'Create Account'}
                    </button>
                  </form>

                  {/* Back to Home */}
                  <div className="text-center mt-4">
                    <Link to="/" className="text-decoration-none">
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Home
                    </Link>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default AuthFormComponent;